import { useState, useCallback, useEffect } from 'react';
import { cva } from 'class-variance-authority';
import { Text } from '@nula/core-ui';
import { NulaLockup } from '@/components/nula-lockup/nula-lockup';
import { isMacDesktop } from '@/lib/platform';
import { useAuthStore } from '@/stores/use-auth-store';
import { saveName, completeOnboarding } from '../api/onboarding';
import { EMPTY_ONBOARDING, type OnboardingData, type OnboardingStep, type UseCase } from '../types/onboarding';
import { AccountFooter } from './account-footer';
import { OnboardingConstellation } from './onboarding-constellation';
import { StepProgress } from './step-progress';
import { openCalendarOAuth } from '@/lib/calendar/calendar-oauth';
import { useCalendarStore } from '@/stores/use-calendar-store';
import { NameStep } from './steps/name-step';
import { AboutStep } from './steps/about-step';
import { CalendarStep } from './steps/calendar-step';
import type { CalendarProvider } from './steps/calendar-step';
import { PermissionsStep } from './steps/permissions-step';
import type { PermissionKind } from './steps/permissions-step';
import { useOnboardingAnalytics } from '../hooks/use-onboarding-analytics';
import styles from './onboarding-flow.module.css';

const root = cva(styles.root);

// Persist the current step so a full-page calendar OAuth redirect (web) returns
// to the same step instead of restarting at the beginning.
const STEP_STORAGE_KEY = 'nula_onboarding_step';

interface OnboardingFlowProps {
  onComplete: (data: OnboardingData) => void;
  onLogout: () => void;
}

export function OnboardingFlow({ onComplete, onLogout }: OnboardingFlowProps) {
  // Calendar connect shows on every platform. The OS-permission step is macOS-
  // only: mic / screen-recording / accessibility are TCC concepts with no
  // Windows or web equivalent, and there is nothing there for a user to grant.
  const steps: OnboardingStep[] = isMacDesktop
    ? ['name', 'calendar', 'permissions', 'about']
    : ['name', 'calendar', 'about'];
  const [index, setIndex] = useState(() => {
    const saved = sessionStorage.getItem(STEP_STORAGE_KEY) as OnboardingStep | null;
    const savedIndex = saved ? steps.indexOf(saved) : -1;
    return savedIndex >= 0 ? savedIndex : 0;
  });
  const [data, setData] = useState<OnboardingData>(EMPTY_ONBOARDING);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [granted, setGranted] = useState<Record<PermissionKind, boolean>>({
    microphone: false,
    system_audio: false,
    accessibility: false,
  });
  const step = steps[index];

  // analytics methods close only over module-level constants (posthog, platform())
  // so no stale-closure risk — no ref or useMemo needed.
  const analytics = useOnboardingAnalytics();

  // Fire onboarding_started exactly once on mount.
  useEffect(() => {
    analytics.started();
    // analytics is a stable functional shape — its methods close over module-level
    // constants only, so omitting it from deps is safe.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fire onboarding_step_viewed each time the active step changes.
  // index+1 is the true 1-based ordinal for the current platform's flow.
  useEffect(() => {
    analytics.stepViewed(step, index + 1);
    // analytics is a stable functional shape — its methods close over module-level
    // constants only, so omitting it from deps is safe.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  useEffect(() => {
    sessionStorage.setItem(STEP_STORAGE_KEY, step);
  }, [step]);

  // Reflect live calendar-connection state so the step shows "Connected" for
  // providers linked during (or before) onboarding.
  const connections = useCalendarStore((s) => s.connections);
  const fetchConnections = useCalendarStore((s) => s.fetchConnections);

  // Load existing connections on mount so a calendar the user already linked
  // (elsewhere, or on a prior visit) shows as Connected rather than offering
  // Connect again.
  useEffect(() => {
    void fetchConnections();
  }, [fetchConnections]);
  const calendarConnected: Record<CalendarProvider, boolean> = {
    google: connections.some((c) => c.provider === 'google'),
    microsoft: connections.some((c) => c.provider === 'microsoft'),
  };

  function handleConnectCalendar(provider: CalendarProvider) {
    void openCalendarOAuth(provider);
  }

  function advanceStep() {
    analytics.stepCompleted(step, index + 1);
    if (index < steps.length - 1) setIndex((i) => i + 1);
    else void handleFinish();
  }

  function skipStep() {
    analytics.stepSkipped(step, index + 1);
    if (index < steps.length - 1) setIndex((i) => i + 1);
    else void handleFinish();
  }

  async function handleNameContinue() {
    if (saving) return;
    setSaving(true);
    setSaveError(null);
    try {
      await saveName(data.name);
      // name is always at index 0 → ordinal 1
      analytics.stepCompleted('name', 1);
      setIndex((i) => i + 1);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to save name. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  async function handleFinish() {
    if (saving) return;
    setSaving(true);
    setSaveError(null);
    try {
      const result = await completeOnboarding({
        useCase: data.useCase,
        referralSource: data.referralSource,
      });
      // Count how many OS permissions were granted. On web this is always 0
      // (the permissions step does not exist in the web flow) — that is correct.
      const permissionsGranted = Object.values(granted).filter(Boolean).length;
      analytics.completed({
        useCase: data.useCase,
        referralSource: data.referralSource,
        permissionsGranted,
      });
      // Flip the boot gate by writing the returned timestamp into the auth store.
      // The store uses Zustand's setState API (same pattern as router.tsx WorkspaceBootstrap).
      useAuthStore.setState({
        onboardingCompletedAt: result.onboardingCompletedAt ?? new Date().toISOString(),
      });
      sessionStorage.removeItem(STEP_STORAGE_KEY);
      onComplete(data);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to complete onboarding. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  const handlePermissionResult = useCallback((kind: PermissionKind, isGranted: boolean) => {
    analytics.permissionResult(kind, isGranted);
    setGranted((prev) => ({ ...prev, [kind]: isGranted }));
  // analytics is a stable functional shape — its methods close over module-level
  // constants only, so omitting it from deps is safe (no re-creation needed).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function handlePermissionPrompted(kind: PermissionKind) {
    analytics.permissionPrompted(kind);
  }

  return (
    <div className={root()}>
      {/* data-tauri-drag-region makes the frameless desktop window draggable by
          these non-interactive surfaces (mirrors login.tsx). Form controls,
          cards, and buttons keep their own click handling. */}
      <div className={styles.left} data-tauri-drag-region>
        <NulaLockup height={32} className={styles.logo} />
        {/* Opt the interactive form out of the drag region so inputs, cards,
            pills, and buttons stay clickable. */}
        <div className={styles.formColumn} data-tauri-drag-region={false}>
          <StepProgress
            total={steps.length}
            current={index}
            // Only completed bars are clickable (StepProgress enforces this);
            // jumping back is always safe — no per-step action is skipped.
            onStepClick={(i) => setIndex(i)}
          />
          {step === 'name' && (
            <NameStep
              value={data.name}
              onChange={(name) => setData((d) => ({ ...d, name }))}
              onContinue={() => void handleNameContinue()}
            />
          )}
          {step === 'about' && (
            <AboutStep
              useCase={data.useCase as UseCase | null}
              referralSource={data.referralSource}
              onUseCase={(useCase) => {
                analytics.useCaseSelected(useCase);
                setData((d) => ({ ...d, useCase }));
              }}
              onReferral={(referralSource) => {
                analytics.referralSelected(referralSource);
                setData((d) => ({ ...d, referralSource }));
              }}
              onContinue={() => {
                analytics.stepCompleted(step, index + 1);
                void handleFinish();
              }}
              onSkip={() => {
                analytics.stepSkipped(step, index + 1);
                void handleFinish();
              }}
            />
          )}
          {step === 'calendar' && (
            <CalendarStep
              connected={calendarConnected}
              onConnect={handleConnectCalendar}
              onContinue={advanceStep}
              onSkip={skipStep}
            />
          )}
          {step === 'permissions' && (
            <PermissionsStep
              onPermissionResult={handlePermissionResult}
              onPermissionPrompted={handlePermissionPrompted}
              onContinue={advanceStep}
              onSkip={skipStep}
            />
          )}
          {/* saveError shown below any step so both saveName and completeOnboarding failures are visible */}
          {saveError && (
            <Text type="danger" className={styles.saveError}>{saveError}</Text>
          )}
        </div>
        <AccountFooter onLogout={() => {
          analytics.abandonedLoginSwitch(step, index + 1);
          onLogout();
        }} />
      </div>
      <OnboardingConstellation step={step} granted={granted} />
    </div>
  );
}
